import { useCallback, useEffect, useMemo, useState } from 'react';
import { LaunchBulkEditTags } from 'src/components/common/BulkActions/BulkEditTagsAction';
import { useBulkEditTagsPermissions } from 'src/components/common/BulkActions/BulkEditTagsAction/useBulkEditTagsPermissions';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import {
  BulkEditTagAction,
  BulkEditTagDialog,
} from 'src/dialogs/BulkEdits/BulkEditTagDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CommandOptionType,
  Event,
  SaleQuery,
} from 'src/WebApiController';

import { SALES_BULK_UPDATE_TAGS_UPDATE_KEY } from '../../SalesActionDropdown.constants';

export const useBulkEditTagsAction = (
  filterQueryWithEventIds: SaleQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditTagsPermissions } = useBulkEditTagsPermissions({
    entityType: ActionOutboxEntityType.Sale,
  });

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `saleTags-event-${event.virtId}`
    : SALES_BULK_UPDATE_TAGS_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditTagDialog,
    closeDialog: closeBulkEditTagDialog,
  } = useDialog(DialogId.BulkEditTags, BulkEditTagDialog);

  const [editTagAction, setEditTagAction] = useState<BulkEditTagAction>(
    BulkEditTagAction.Update
  );

  const onSubmitEditTags = useCallback(
    async (
      action: BulkEditTagAction,
      tagKey: string | null,
      tagValue?: string | undefined,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }

      setIsLoading(true);

      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditSaleClient(activeAccountWebClientConfig);

            const preview = await client.bulkUpdateTagsPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTagsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (tagKey) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditSaleClient(activeAccountWebClientConfig);

            const succeeded =
              action === BulkEditTagAction.Update
                ? await client.bulkUpdateTags(
                    preview!.preview,
                    tagKey,
                    tagValue,
                    updateKey,
                    supportBackgroundProcess
                  )
                : await client.bulkRemoveTags(
                    preview!.preview,
                    tagKey,
                    updateKey,
                    supportBackgroundProcess
                  );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on details modal
                // we force re-loading the entity and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditTagDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateTags', error, {
              trackErrorData: { tagKey, tagValue, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      setIsLoading,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkEditTagDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeTagClick = useCallback(
    (e: React.MouseEvent, editTagAction: BulkEditTagAction) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      setEditTagAction(editTagAction);
      openBulkEditTagDialog({
        updateKey,
        action: editTagAction,
        onOkay: onSubmitEditTags,
        entityType: ActionOutboxEntityType.Sale,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditTagDialog,
      });
    },
    [
      closeBulkEditTagDialog,
      event,
      onSubmitEditTags,
      openBulkEditTagDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openAddTagDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    setEditTagAction(BulkEditTagAction.Update);
    openBulkEditTagDialog({
      updateKey,
      action: BulkEditTagAction.Update,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Sale,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  const openRemoveTagDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    setEditTagAction(BulkEditTagAction.Remove);
    openBulkEditTagDialog({
      updateKey,
      action: BulkEditTagAction.Remove,
      onOkay: onSubmitEditTags,
      entityType: ActionOutboxEntityType.Sale,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditTagDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditAddTags,
        onSelect: openAddTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions,
    });

    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditRemoveTags,
        onSelect: openRemoveTagDialogStableRef,
      },
      disabled: !hasEditTagsPermissions,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditAddTags,
        disabled: !hasEditTagsPermissions,
      });
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditRemoveTags,
        disabled: !hasEditTagsPermissions,
      });
    };
  }, [
    hasEditTagsPermissions,
    openAddTagDialogStableRef,
    openRemoveTagDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditTagsPermissions) {
      return [];
    }

    return [
      {
        title: ContentId.UpdateTagValues,
        onClick: (e: React.MouseEvent) => {
          onChangeTagClick(e, BulkEditTagAction.Update);
        },
        disabled: isLoading,
      },
      {
        title: ContentId.RemoveTagValues,
        onClick: (e: React.MouseEvent) => {
          onChangeTagClick(e, BulkEditTagAction.Remove);
        },
        disabled: isLoading,
      },
    ];
  }, [hasEditTagsPermissions, isLoading, onChangeTagClick]);

  return {
    dropDown: (
      <LaunchBulkEditTags
        key="EditTags"
        onClick={onChangeTagClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Sale}
      />
    ),
    actionItems,
  };
};
